import { core } from '../core/index.js';
import { filterFabricErrors } from '../errors/errors.js';
import { type FabricError } from '../fabric-error/index.js';

export type ToolbarNavbarItemWithIcon = {
  /**
   * 按钮大小 IOS 30 X 30 默认尺寸
   * Note: ICON设计准则: 周边留白为24px, 内容空间80 X 80; 总尺寸128 * 128为原图
   * https://www.iconfont.cn/collections/detail?spm=a313x.7781069.1998910419.d9df05512&cid=16472
   * type===`icon`
   */
  iconSize?: number;
};

export type ToolbarNavbarItemWithText = {
  /**
   * navbar字体大小
   * @default 18
   */
  fontSize?: number;
  /**
   * 字体样式. 加粗, 斜体, 下划线.
   */
  fontStyle?: Array<'bold' | 'italic' | 'underline'>;
  /**
   * navbar字体颜色
   */
  fontColor?: string;
  /**
   * 仅`iOS`支持
   * 字体权重, 支持字符串和数字，字符串支持,
   * 字符串类型: `ultraLight，thin，light，regular，medium，semibold，bold，heavy，black`
   * 数字类型: `1-1000`，`包含1和1000`
   * 字体权重
   */
  fontWeight?:
    | 'ultraLight'
    | 'thin'
    | 'light'
    | 'regular'
    | 'medium'
    | 'semibold'
    | 'bold'
    | 'heavy'
    | 'black'
    | number;
};

export type ToolbarNavbarItemType = 'icon' | 'text';

export type ToolbarNavbarItem<T extends ToolbarNavbarItemType> =
  (T extends 'icon' ? ToolbarNavbarItemWithIcon : ToolbarNavbarItemWithText) & {
    /**
     * 自定义内容, 用来标识当前传递的内容项, 当自定义区域按钮点击时间的时候会返回这个tag用来标识当前点击的项
     */
    tag?: string;
    /**
     * 定义一个标记, 标识当前的item与默认的back按钮行为一致
     * @default undefined
     */
    asBackButton?: boolean;
    /**
     * 类型, `icon`|`text`
     */
    type: T;
    /**
     * type: 'icon'
     * https://www.iconfont.cn/collections/detail?spm=a313x.7781069.1998910419.d9df05512&cid=16472
     * `data:image/png;base64,iVBORw0K....` 如果是图片传递需要去掉`data:image/png;base64,`
     */
    value: string;
    /**
     * 左边距
     */
    marginLeft?: number;
    /**
     * 右边距,
     */
    marginRight?: number;
  };

export type FabricToolbarNavbarOptions<
  T1 extends ToolbarNavbarItemType,
  T2 extends ToolbarNavbarItemType,
  T3 extends ToolbarNavbarItemType,
> = {
  /**
   * 状态栏/navbar背景色透明度, 采用字符串, 考虑到android内部number默认有值0, 不能区分是否为更新的设置的0还是默认值.
   * 0-255
   */
  opacity?: string;
  /**
   * 状态栏/navbar的背景色
   */
  bgColor?: string;
  /**
   * 线性梯度渐变. 优先级在bgColor之上.
   * 网页的rgba, 最后2位数是代表透明度. 取值16进制从00 -> FF（越小越透明）,00表示完全透明, FF就是全不透明, 后面六位是色值
   * 需要转换成Native约定的ARGB格式, 透明度放前2位
   * background: -webkit-linear-gradient(top, #CE5937FF 0%, #1C6EA4FF 42%, #C59237FF 100%);
   * 公式如: background: rgba(125, 0, 0, .3); 表示的是30%不透明度的红色背景。把30%的不透明度转换成十六制呢的方法如下：先计算#AA的的十进制x，x/255 = 3/10，解得x=3*255/10，然后再把x换算成十六进制，约等于4C。
   * 常用透明度对应16进制关系
   * ```
   * .0（00） .1（19） .05（0C） .15（26）
   * .2（33） .3（4C） .25（3F） .35（59）
   * .4（66） .5（7F） .45（72） .55（8C）
   * .6（99） .7（B2） .65（A5） .75（BF）
   * .8（CC） .9（E5） .85（D8） .95（F2）
   * ```
   * @see https://html-css-js.com/css/generator/gradient/
   * @example
   * ```ts
   * {
   * direction: "top",
   * gradients:[
   *   ["#FFCE5937", 0]
   *   ["#FF1C6EA4", 0.42]
   *   ["#FFC59237", 1]
   *  ]
   * }
   * ```
   */
  linearGradient?: {
    /**
     * 方位起点'top'|'left'
     */
    direction: 'top' | 'left';
    /**
     * 渐变数据
     */
    gradients: Array<[string, number]>;
  };
  /**
   * 当自定义toolbar的back按钮的时候, 是否开启当前webview返回按钮默认行为支持浏览器栈(默认开启)
   * 当开启: 当前webview的`locationTo`(location.href)产生的栈, 返回默认优先返回浏览器的栈, 待无浏览器栈的时候再掉用户自定义callback(如果为定义callback, 直接返回上一级)
   * 当未开启: 点back按钮直接退出当前webview
   * 注意Note: 如果用户未配置navbar此时的back按钮应该保持默认支持浏览器栈的行为能力.
   * 注意Note: 如果当前没有`asBackButton`配置, 如果通过`asBackButton`找到最后一次back应该响应JS注册回调时间, 如果找不到`asBackButton` Native
   * 会尝试找默认back按钮(`left`未被占用)此时走native默认back行为.
   * @default true
   */
  browserHistory?: boolean;
  /**
   * 状态栏风格
   */
  statusBarStyle?: 'dark' | 'light';
  /**
   * navbar布局顺序, 默认值: `right-left-center(最后一个剩余空间(`center`)居中)`.
   * 如果需要让中间内容居中`left-center-right(最后一个剩余空间(`right`)居右)`或者`right-center-left(最后一个剩余空间`left`居左)`
   * @default `left-center-right` (形态2)
   */
  layout?:
    | 'left-center-right' // 形态1
    | 'right-center-left' // 形态1
    | 'left-right-center' // 形态2
    | 'right-left-center' // 形态2
    | 'center-left-right' // 形态3
    | 'center-right-left'; // 形态3
  /**
   * left[0]=左边区域左侧位置,left[1]=左边区域右侧位置
   */
  left?: ToolbarNavbarItem<T1>[];
  /**
   *  center[0]=中间区域左侧位置,center[1]=中间区域右侧位置
   */
  center?: ToolbarNavbarItem<T2>[];
  /**
   * right[0]=右边区域左侧位置,right[1]=右边区域右侧位置
   */
  right?: ToolbarNavbarItem<T3>[];
};

export type ToolbarCallbackAction = 'reset' | 'update';

export type FabricToolbarSetNavbarApi = <
  T1 extends ToolbarNavbarItemType,
  T2 extends ToolbarNavbarItemType,
  T3 extends ToolbarNavbarItemType,
>(
  options: FabricToolbarNavbarOptions<T1, T2, T3>,
  callback?:
    | ((
        err: FabricError | null,
        clickedItemTag: string,
        action: ToolbarCallbackAction
      ) => void)
    | undefined
) => void;

/**
 * 设置navbar; URL参数协议均为webview初始化的时候执行.
 * 如果开启float模式则需要在H5 url上增加`ntv_float`参数,注意参数是获取queryKey匹配, 不能简单粗暴路径匹配.
 * 如: 匹配`ntv_float` `http://domain.com/home/index?ntv_float&name=tian` 匹配成功, `http://domain.com/home/index?name=ntv_float`则匹配为失败
 *
 * 1. ntv_float: 表示标题栏浮动不占据页面空间, 如果float模式，默认标题栏一开始就是纯透明的
 * 2. ntv_login: 表示进入当前页面之前判断是否登录，未登录，先登录再跳转进入页面
 * 3. ntv_new: URL链接打开新窗口
 * 4. ntv_hidden: 隐藏标题栏
 * 5. ntv_external:启动系统浏览器打开(ntv_external)
 * 6. ntv_history: Webview打开外部网页,使浏览器Back按钮(ntv_history)可逐级返回
 * 7. queryString('ntv_float')只要存在则默认为true无需写成?ntv_float=1:
 * 8. 原生页面跳转支持: xxclient://home?ntv_float&ntv_login&ntv_hidden
 * 9. webview h5页面跳转支持: https://domain.com/home/index?ntv_float&ntv_login&ntv_hidden
 *
 * @example
 *
 * ```html
 * https://domain.com/home/index?ntv_float&ntv_login
 * ```
 * 注意数据内容的配置与UI上`left`,`center`,`right`保持一致.
 *
 * @param options FabricToolbarNavbarOptions
 */
export const setNavbar: FabricToolbarSetNavbarApi = <
  T1 extends ToolbarNavbarItemType,
  T2 extends ToolbarNavbarItemType,
  T3 extends ToolbarNavbarItemType,
>(
  options: FabricToolbarNavbarOptions<T1, T2, T3>,
  callback?: (
    err: FabricError | null,
    clickedItemTag: string,
    action: ToolbarCallbackAction
  ) => void
) => {
  core.call<
    'fabric_toolbar.setNavbar',
    FabricToolbarNavbarOptions<T1, T2, T3>,
    string
  >(
    'fabric_toolbar.setNavbar',
    {
      ...options,
      layout: options.layout || 'left-center-right',
      browserHistory:
        typeof options.browserHistory === 'undefined'
          ? true
          : options.browserHistory,
    },
    callback
      ? (result) => {
          const fabricErrors = filterFabricErrors(
            result.code,
            result.message,
            result.data
          );
          // 如果code==='1', 约定当前为清理上一次的callback函数使用, 不做业务回调处理.
          // 当多次调用setNavbar, updateNavbar时候避免内存泄漏问题.
          if (fabricErrors?.code === '1') {
            return;
          }
          callback(fabricErrors || null, result.data, 'reset');
        }
      : undefined
  );
};
